import {
  UploadUrlResponse,
  UploadResponse,
  SplitAndClassifyRequest,
  SplitAndClassifyResponse,
  InvalidRequestErrorResponse,
  BankStatementExtractRequest,
  BankStatementExtractResponse
} from './types';
import { config, endpoints } from './config';

export class ApiError extends Error {
  constructor(public status: number, message?: string, public detail?: any) {
    const messageText = message || detail?.[0].msg || 'Unknown error';
    const formatted_message = `${status} ${messageText}`;
    super(formatted_message);
  }
}

function getHeaders(authToken?: string): HeadersInit {
  const headers: HeadersInit = {};
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  return headers;
}

async function handleErrorResponse(response: Response, fallbackMessage: string): Promise<never> {
  try {
    const errorData: InvalidRequestErrorResponse = await response.json();
    throw new ApiError(response.status, errorData.message, errorData.detail);
  } catch (parseError) {
    if (parseError instanceof ApiError) {
      // If JSON parsing fails, it might be a non-JSON error response
      throw parseError;
    }
    // If JSON parsing fails, use fallback message
    throw new ApiError(response.status, fallbackMessage);
  }
}

export async function generateUploadUrl(authToken?: string, contentSize?: number, contentType?: string): Promise<UploadUrlResponse> {
  const response = await fetch(`${config.apiBase}${endpoints.generateUploadUrl}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getHeaders(authToken),
    },
    body: JSON.stringify({
      content_size: contentSize,
      content_type: contentType
    }),
  });
  
  if (!response.ok) {
    await handleErrorResponse(response, 'Failed to generate Upload URL');
  }
  
  return response.json();
}

export async function uploadFile(file: File, authToken?: string): Promise<UploadResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.uploadTimeout);

  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${config.apiBase}${endpoints.upload}`, {
      method: 'POST',
      body: formData,
      headers: getHeaders(authToken),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      await handleErrorResponse(response, 'Failed to upload file');
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(408, 'Upload timeout - file upload took too long');
    }
    throw error;
  }
}

export async function uploadFileWithUrl(uploadUrlResponse: UploadUrlResponse, file: File) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.uploadTimeout);

  try {
    const requestInit: RequestInit = {
      signal: controller.signal,
      redirect: 'follow',
    };
    switch (uploadUrlResponse.method.toUpperCase()) {
      case 'POST':
        requestInit.method = 'POST';
        const formData = new FormData();
        for (const [key, value] of Object.entries(uploadUrlResponse.fields || {})) {
          formData.append(key, value);
        }
        formData.append('file', file);
        requestInit.body = formData;
        break;
      default:
        requestInit.method = 'PUT';
        requestInit.headers = {
          'Content-Type': file.type,
          'Content-Length': file.size.toString(),
        };
        requestInit.body = file;
        break;
    }
    const response = await fetch(uploadUrlResponse.url, requestInit);
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      await handleErrorResponse(response, 'Failed to upload file');
    }
    return;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(408, 'Upload timeout - file upload took too long');
    }
    throw error;
  }
}

export async function splitAndClassify(request: SplitAndClassifyRequest, authToken?: string): Promise<SplitAndClassifyResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.processingTimeout);

  try {
    const headers = {
      'Content-Type': 'application/json',
      ...getHeaders(authToken),
    };

    const response = await fetch(`${config.apiBase}${endpoints.splitAndClassify}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      await handleErrorResponse(response, 'Failed to split and classify document');
    }
    
    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(408, 'Processing timeout - document processing took too long');
    }
    throw error;
  }
}

export async function ping(authToken?: string): Promise<{ result: string }> {
  const response = await fetch(`${config.apiBase}${endpoints.ping}`, {
    headers: getHeaders(authToken),
  });
  
  if (!response.ok) {
    await handleErrorResponse(response, 'Ping failed');
  }
  
  return response.json();
}

export async function bankStatementExtract(request: BankStatementExtractRequest, authToken?: string): Promise<BankStatementExtractResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.processingTimeout);

  try {
    const headers = {
      'Content-Type': 'application/json',
      ...getHeaders(authToken),
    };

    const response = await fetch(`${config.apiBase}${endpoints.bankStatementExtract}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      await handleErrorResponse(response, 'Failed to extract bank statement');
    }
    
    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiError(408, 'Processing timeout - bank statement extraction took too long');
    }
    throw error;
  }
}
